class InfiniteScroll extends HTMLElement {
  constructor() {
    super();

    this._selectors = {
      nextPage: '.pagination-custom__next:not(.is-inactive)',
      grid: '[data-products-grid]',
      pagination: '.pagination-custom'
    };

    this.loading = false;
    this.observer = null;
    this.loadTrigger = null;
  }

  connectedCallback() {
    this._retainHistory();
    this._initInfiniteScroll();

    // Wait for DOM to be ready
    setTimeout(() => {
      this.initInfiniteScroll();
    }, 0);
  }

  _retainHistory = () => {
    if (!history.state) {
      return;
    }

    const grid = this.querySelector(this._selectors.grid);
    const gridInner = grid ? grid.querySelector('#CollectionLoop') : null;

    if (history.state.products && gridInner) {
      gridInner.innerHTML = history.state.products;
    }

    if (history.state.view && typeof this._updateGridView === 'function') {
      this._updateGridView(history.state.view, false);
    }
  }

  _updateHistory(key, value, url) {
    let state = {};

    if (history.state) {
      state = history.state;
    }

    state[key] = value;

    if (url) {
      history.replaceState(state, '', url);
    } else {
      history.replaceState(state, '');
    }
  }

  _initInfiniteScroll() {
    const pagination = this.querySelector(this._selectors.pagination);
    const nextPage = this.querySelector(this._selectors.nextPage);
    
    if (!pagination || !nextPage) {
      return;
    }

    // Hide pagination visually
    pagination.style.display = 'none';

    // Create load trigger element at the bottom of the grid
    this._createLoadTrigger();

    const options = {
      root: null,
      threshold: 0.5,
      rootMargin: '100px'
    };

    this.observer = new IntersectionObserver(async (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !this.loading) {
          this.loading = true;

          const currentNextPage = this.querySelector(this._selectors.nextPage);
          if (!currentNextPage) {
            this.loading = false;
            this._disconnectObserver();
            return;
          }

          await this._addProducts(currentNextPage);

          this._disconnectObserver();
          setTimeout(() => {
            this._initInfiniteScroll();
          }, 100);
        }
      }
    }, options);

    if (this.loadTrigger) {
      this.observer.observe(this.loadTrigger);
    }
  }

  _createLoadTrigger() {
    const grid = this.querySelector(this._selectors.grid);
    if (!grid) return;

    if (this.loadTrigger && this.loadTrigger.parentNode) {
      this.loadTrigger.parentNode.removeChild(this.loadTrigger);
    }

    this.loadTrigger = document.createElement('div');
    this.loadTrigger.style.height = '1px';
    this.loadTrigger.style.position = 'absolute';
    this.loadTrigger.style.bottom = '0';
    grid.appendChild(this.loadTrigger);
  }

  _disconnectObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.loadTrigger && this.loadTrigger.parentNode) {
      this.loadTrigger.parentNode.removeChild(this.loadTrigger);
    }
  }

  async _addProducts(nextPage) {
    let url = nextPage.getAttribute('href');
    
    if (!url) {
      this.loading = false;
      return;
    }

    const grid = this.querySelector(this._selectors.grid);
    const gridInner = grid ? grid.querySelector('#CollectionLoop') : null;

    if (!grid || !gridInner) {
      this.loading = false;
      return;
    }

    grid.classList.add('is-loading');

    try {
      const response = await fetch(url);
      const html = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const newGridInner = doc.querySelector('#CollectionLoop');
      const newPagination = doc.querySelector('.pagination-custom');
      
      if (!newGridInner) {
        this.loading = false;
        grid.classList.remove('is-loading');
        return;
      }

      // Get all products from the new page
      const products = newGridInner.querySelectorAll('.grid-item');
      
      if (products.length === 0) {
        grid.classList.remove('is-loading');
        this.loading = false;
        return;
      }

      products.forEach(product => {
        gridInner.appendChild(product);
      });

      this._updateHistory('products', gridInner.innerHTML, url);

      // Dispatch event for product count update (outside infinite-scroll element)
      document.dispatchEvent(new CustomEvent('products:loaded'));

      if (newPagination) {
        const existingPagination = this.querySelector('.pagination-custom');
        if (existingPagination && existingPagination.parentNode) {
          existingPagination.replaceWith(newPagination);
          newPagination.style.display = 'none';
        }
      } else {
        const existingPagination = this.querySelector('.pagination-custom');
        if (existingPagination) {
          existingPagination.remove();
        }
      }

    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      this.loading = false;
      grid.classList.remove('is-loading');
    }
  }

  disconnectedCallback() {
    this.disconnectObserver();
  }
}

customElements.define('infinite-scroll', InfiniteScroll);

